import React, { useState, useEffect } from 'react';
import { Search, Star, MapPin, Globe, Clock, Users, Filter, X, Flame, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const EXPERIENCE_LABELS = { benevole: 'Bénévole', professionnel: 'Professionnel', expert_certifie: 'Expert certifié' };
const INTERVENTION_LABELS = { distanciel: 'Distanciel', presentiel: 'Présentiel', les_deux: 'Distanciel & Présentiel' };
const AVAILABILITY_LABELS = { this_week: 'Cette semaine', this_month: 'Ce mois', weekends: 'Week-ends', flexible: 'Flexible' };

function MentorCard({ mentor, onClick }) {
  return (
    <div
      onClick={() => onClick(mentor.id)}
      data-testid={`mentor-card-${mentor.id}`}
      className="kurso-card p-4 cursor-pointer hover:border-[#ff6b6b]/30 transition-all group"
    >
      {mentor.is_featured && (
        <div className="text-xs text-[#ffd93d] font-semibold mb-2 flex items-center gap-1">
          <Star size={10} fill="currentColor" /> Mentor mis en avant
        </div>
      )}
      <div className="flex gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-lg font-bold flex-shrink-0 overflow-hidden">
          {mentor.avatar ? <img src={mentor.avatar} alt={mentor.name} className="w-full h-full object-cover" /> : mentor.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm group-hover:text-[#ff6b6b] transition-colors truncate">{mentor.name}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            {mentor.avg_rating > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-[#ffd93d]">
                <Star size={10} fill="currentColor" /> {mentor.avg_rating.toFixed(1)} ({mentor.review_count})
              </span>
            )}
            <span className="text-xs text-[#a0a0ab]">{EXPERIENCE_LABELS[mentor.experience_level] || mentor.experience_level}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {mentor.has_free_offer ? (
            <span className="text-[#06d6a0] text-xs font-semibold">Gratuit</span>
          ) : mentor.min_price > 0 ? (
            <span className="text-white text-xs">À partir de<br /><strong className="text-base">{mentor.min_price}€</strong></span>
          ) : null}
        </div>
      </div>

      {mentor.bio && <p className="text-[#a0a0ab] text-xs mb-3 line-clamp-2">{mentor.bio}</p>}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {(mentor.specialities || []).slice(0, 3).map(s => (
          <span key={s} className="text-xs bg-white/5 text-[#a0a0ab] px-2 py-0.5 rounded-lg">{s}</span>
        ))}
        {(mentor.specialities || []).length > 3 && (
          <span className="text-xs text-[#a0a0ab]">+{mentor.specialities.length - 3}</span>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-[#a0a0ab]">
        {mentor.location_city && (
          <span className="flex items-center gap-1"><MapPin size={10} /> {mentor.location_city}</span>
        )}
        <span className="flex items-center gap-1"><Globe size={10} /> {INTERVENTION_LABELS[mentor.intervention_type]}</span>
        {mentor.total_sessions > 0 && (
          <span className="flex items-center gap-1"><Users size={10} /> {mentor.total_sessions} sessions</span>
        )}
      </div>
    </div>
  );
}

export default function Mentors() {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: '', speciality: '', intervention_type: '', language: '',
    availability: '', experience_level: '', min_rating: '', sort_by: 'pertinence',
  });

  const fetchMentors = async (params = filters) => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''));
      const res = await axios.get(`${API}/api/mentors`, { params: clean });
      setMentors(res.data.mentors || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setMentors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMentors(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMentors();
  };

  const updateFilter = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const clearFilters = () => {
    const reset = { q: '', speciality: '', intervention_type: '', language: '', availability: '', experience_level: '', min_rating: '', sort_by: 'pertinence' };
    setFilters(reset);
    fetchMentors(reset);
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== 'sort_by' && k !== 'q' && v !== '').length;

  return (
    <Layout>
      <Helmet>
        <title>Mentors — Kurso</title>
        <meta name="description" content="Apprenez avec un mentor Kurso. Trouvez un expert selon votre spécialité, disponibilité et budget." />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-heading font-bold text-white mb-2">Apprenez avec un mentor</h1>
          <p className="text-[#a0a0ab]">Les mentors Kurso sont des facilitateurs bénévoles ou indépendants. Leurs conseils ne remplacent en aucun cas l'avis d'un professionnel qualifié (médecin, juriste, psychologue, thérapeute, comptable, conseiller financier, ou tout autre expert certifié dans son domaine). Kurso agit uniquement comme plateforme de mise en relation et décline toute responsabilité quant aux conseils prodigués par les mentors.</p>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
            <input
              type="text" placeholder="Rechercher par nom, spécialité, compétence..."
              value={filters.q} onChange={e => updateFilter('q', e.target.value)}
              data-testid="mentor-search-input"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50"
            />
          </div>
          <button type="button" onClick={() => setShowFilters(!showFilters)}
            data-testid="mentor-filter-btn"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition-colors ${showFilters || activeFilterCount > 0 ? 'border-[#ff6b6b] text-[#ff6b6b] bg-[#ff6b6b]/10' : 'border-white/10 text-[#a0a0ab] hover:border-white/20'}`}>
            <Filter size={14} /> Filtres {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
          <button type="submit" className="bg-[#ff6b6b] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#ff5555] transition-colors">
            Rechercher
          </button>
        </form>

        {/* Filters panel */}
        {showFilters && (
          <div className="kurso-card p-4 mb-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <select value={filters.intervention_type} onChange={e => updateFilter('intervention_type', e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs">
              <option value="">Type d'intervention</option>
              <option value="distanciel">Distanciel</option>
              <option value="presentiel">Présentiel</option>
              <option value="les_deux">Les deux</option>
            </select>
            <select value={filters.availability} onChange={e => updateFilter('availability', e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs">
              <option value="">Disponibilité</option>
              {Object.entries(AVAILABILITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={filters.experience_level} onChange={e => updateFilter('experience_level', e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs">
              <option value="">Niveau d'expérience</option>
              {Object.entries(EXPERIENCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={filters.min_rating} onChange={e => updateFilter('min_rating', e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs">
              <option value="">Note minimum</option>
              <option value="3">3★ et plus</option>
              <option value="4">4★ et plus</option>
              <option value="4.5">4.5★ et plus</option>
            </select>
            <select value={filters.sort_by} onChange={e => updateFilter('sort_by', e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs">
              <option value="pertinence">Trier : Pertinence</option>
              <option value="note">Note</option>
              <option value="sessions">Nombre de sessions</option>
            </select>
            <button onClick={clearFilters} className="flex items-center justify-center gap-1 border border-white/10 rounded-xl px-3 py-2 text-[#a0a0ab] text-xs hover:text-white transition-colors">
              <X size={12} /> Effacer les filtres
            </button>
          </div>
        )}

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#a0a0ab]">{loading ? 'Chargement...' : `${total} mentor${total !== 1 ? 's' : ''} disponible${total !== 1 ? 's' : ''}`}</p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="kurso-card p-4 h-48 animate-pulse" />)}
          </div>
        ) : mentors.length === 0 ? (
          <div className="text-center py-16">
            <Users size={40} className="text-[#a0a0ab] mx-auto mb-4" />
            <p className="text-white font-semibold mb-2">Aucun mentor trouvé</p>
            <p className="text-[#a0a0ab] text-sm">Essayez d'autres critères de recherche</p>
            <button onClick={clearFilters} className="mt-4 text-[#ff6b6b] text-sm hover:underline">Effacer les filtres</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mentors.map(m => <MentorCard key={m.id} mentor={m} onClick={id => navigate(`/mentors/${id}`)} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
